/**
 * ClustersTab — Thematic groupings of clips within a project.
 *
 * Shows cluster cards with their member clips. Users can create
 * clusters, add/remove clips, and rename them. Clips can be
 * drag-and-drop reordered within each cluster.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, Layers, Film, X, Trash2, Edit2, Check, Sparkles, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDndSensors } from '../../hooks/useDndSensors'
import apiClient from '../../services/api'
import MediaPreviewModal from '../MediaPreviewModal'
import { useMediaPreview } from '../../hooks/useMediaPreview'
import { apiErrorDetail } from '../../utils/dataValidator'

interface MomentBrief {
  id: number
  scene_index: number
  title: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  asset_filename: string | null
}

interface ClusterMoment {
  id: number
  scene_index: number
  title: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  asset_filename: string | null
  asset_id: number | null
  asset_file_type: string | null
}

interface ClusterItem {
  id: number
  project_id: string
  name: string
  description: string | null
  moments_count: number
  moments: ClusterMoment[]
  created_time: string | null
}

interface ClustersTabProps {
  projectIds: string[]
  onGenerateDraft?: (scope: { scope_type: string; scope_ref_id: number; label: string; project_id: string }) => void
}

// ---------------------------------------------------------------------------
// Sortable clip card
// ---------------------------------------------------------------------------

function SortableClusterClip({
  moment,
  onRemove,
  onPreview,
}: {
  moment: ClusterMoment
  onRemove: () => void
  onPreview: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: moment.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex-shrink-0 w-40 bg-black/25 border border-white/10 rounded-lg overflow-hidden group relative"
    >
      <div
        className="aspect-video bg-black/35 relative cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        {moment.thumbnail_url ? (
          <img
            src={moment.thumbnail_url}
            alt={moment.title || 'Clip'}
            className="w-full h-full object-cover pointer-events-none"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
          </div>
        )}
        <button
          onClick={e => { e.stopPropagation(); onPreview() }}
          onPointerDown={e => e.stopPropagation()}
          className="absolute top-1 left-1 bg-black/70 text-white/80 hover:text-white p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Preview"
        >
          <Eye className="w-3 h-3" />
        </button>
        <button
          onClick={e => { e.stopPropagation(); onRemove() }}
          onPointerDown={e => e.stopPropagation()}
          className="absolute top-1 right-1 bg-black/70 text-[var(--rq-text-soft)] hover:text-red-400 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Remove from cluster"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="p-1.5">
        <p className="text-xs text-[var(--rq-text-soft)] truncate">
          {moment.title || moment.asset_filename || 'Untitled'}
        </p>
      </div>
    </div>
  )
}

function ClusterClipOverlay({ moment }: { moment: ClusterMoment }) {
  return (
    <div className="flex-shrink-0 w-40 bg-black/25 border border-white/10 rounded-lg overflow-hidden ring-2 ring-[var(--btn)] rotate-2 shadow-xl">
      <div className="aspect-video bg-black/35">
        {moment.thumbnail_url ? (
          <img
            src={moment.thumbnail_url}
            alt={moment.title || 'Clip'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
          </div>
        )}
      </div>
      <div className="p-1.5">
        <p className="text-xs text-[var(--rq-text-soft)] truncate">
          {moment.title || moment.asset_filename || 'Untitled'}
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ClustersTab({ projectIds, onGenerateDraft }: ClustersTabProps) {
  const [clusters, setClusters] = useState<ClusterItem[]>([])
  const [moments, setMoments] = useState<MomentBrief[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create cluster state
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')

  // Inline edit state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  // Add clips to existing cluster modal
  const [addToClusterId, setAddToClusterId] = useState<number | null>(null)
  const [addSelectedIds, setAddSelectedIds] = useState<Set<number>>(new Set())
  const [addingClips, setAddingClips] = useState(false)

  // DnD state
  const [activeClipId, setActiveClipId] = useState<number | null>(null)
  const clustersSnapshot = useRef<ClusterItem[]>([])
  const sensors = useDndSensors()

  const { preview, closePreview, openFromMoment } = useMediaPreview()

  const activeMoment = activeClipId
    ? clusters.flatMap(c => c.moments).find(m => m.id === activeClipId) ?? null
    : null

  const fetchData = useCallback(async () => {
    if (projectIds.length === 0) return
    try {
      setLoading(true)
      setError(null)
      const projectsParam = projectIds.join(',')
      const [clusRes, momRes] = await Promise.all([
        apiClient.get('/clusters/', { params: { project_ids: projectsParam } }),
        apiClient.get('/moments/', { params: { project_ids: projectsParam, limit: 200 } }),
      ])
      setClusters(clusRes.data.clusters)
      setMoments(momRes.data.moments)
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load clusters'))
    } finally {
      setLoading(false)
    }
  }, [projectIds])

  useEffect(() => { fetchData() }, [fetchData])

  const createCluster = async () => {
    if (projectIds.length !== 1) {
      toast.error('Select exactly one workspace to create a cluster')
      return
    }
    if (!newName.trim()) return
    try {
      await apiClient.post(`/clusters/?project_id=${projectIds[0]}`, {
        name: newName.trim(),
        description: newDesc.trim() || null,
      })
      setNewName('')
      setNewDesc('')
      setShowCreate(false)
      toast.success('Cluster created')
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to create cluster'))
    }
  }

  const deleteCluster = async (id: number) => {
    try {
      await apiClient.delete(`/clusters/${id}`)
      toast.success('Cluster deleted')
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete cluster'))
    }
  }

  const saveEdit = async (id: number) => {
    if (!editName.trim()) return
    try {
      await apiClient.patch(`/clusters/${id}`, { name: editName.trim() })
      setEditingId(null)
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to update cluster'))
    }
  }

  const removeMoment = async (clusterId: number, momentId: number) => {
    try {
      await apiClient.delete(`/clusters/${clusterId}/moments/${momentId}`)
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to remove clip'))
    }
  }

  // --- Add clips to existing cluster ---

  const openAddClipsModal = (clusterId: number) => {
    setAddToClusterId(clusterId)
    setAddSelectedIds(new Set())
    setAddingClips(false)
  }

  const closeAddClipsModal = () => {
    setAddToClusterId(null)
    setAddSelectedIds(new Set())
  }

  const toggleMomentForAdd = (id: number) => {
    setAddSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const addClipsToCluster = async () => {
    if (addToClusterId === null || addSelectedIds.size === 0) return
    try {
      setAddingClips(true)
      await apiClient.post(`/clusters/${addToClusterId}/moments`, {
        moment_ids: Array.from(addSelectedIds),
      })
      toast.success(`Added ${addSelectedIds.size} clip${addSelectedIds.size !== 1 ? 's' : ''} to cluster`)
      closeAddClipsModal()
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to add clips'))
    } finally {
      setAddingClips(false)
    }
  }

  // --- DnD handlers ---

  const handleDragStart = (event: DragStartEvent) => {
    clustersSnapshot.current = clusters.map(c => ({ ...c, moments: [...c.moments] }))
    setActiveClipId(Number(event.active.id))
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveClipId(null)
    if (!over || active.id === over.id) return

    const activeContainer = active.data.current?.sortable?.containerId
    const overContainer = over.data.current?.sortable?.containerId
    if (!activeContainer || !overContainer || activeContainer !== overContainer) return

    const clusterId = Number(String(activeContainer).replace('cluster-', ''))
    const cluster = clusters.find(c => c.id === clusterId)
    if (!cluster) return

    const oldIndex = cluster.moments.findIndex(m => m.id === Number(active.id))
    const newIndex = cluster.moments.findIndex(m => m.id === Number(over.id))
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

    const reordered = arrayMove(cluster.moments, oldIndex, newIndex)
    setClusters(prev => prev.map(c =>
      c.id === clusterId ? { ...c, moments: reordered } : c
    ))

    try {
      await apiClient.patch(`/clusters/${clusterId}/reorder`, {
        moment_ids: reordered.map(m => m.id),
      })
    } catch {
      toast.error('Failed to reorder clips')
      setClusters(clustersSnapshot.current)
    }
  }

  const handleDragCancel = () => {
    setClusters(clustersSnapshot.current)
    setActiveClipId(null)
  }

  // --- Computed ---

  const addToCluster = addToClusterId !== null ? clusters.find(c => c.id === addToClusterId) : null
  const existingMomentIds = addToCluster ? new Set(addToCluster.moments.map(m => m.id)) : new Set<number>()
  const availableForAdd = moments.filter(m => !existingMomentIds.has(m.id))

  if (projectIds.length === 0) {
    return (
      <div className="text-center py-16">
        <Layers className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">Select a workspace</h2>
        <p className="text-[var(--rq-text-soft)]">Choose a workspace above to manage clusters</p>
      </div>
    )
  }

  if (loading) return <div className="text-center py-16 text-[var(--rq-text-soft)]">Loading clusters...</div>
  if (error) return <div className="text-center py-16 text-red-400">{error}</div>

  return (
    <div>
      {/* Header with create button */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[var(--rq-text-soft)]">
          {clusters.length} cluster{clusters.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={() => {
            if (projectIds.length !== 1) {
              toast.error('Select exactly one workspace to create a cluster')
              return
            }
            setShowCreate(true)
          }}
          disabled={projectIds.length !== 1}
          className="rq-btn-primary px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          title={projectIds.length !== 1 ? 'Select exactly one workspace' : undefined}
        >
          <Plus className="w-4 h-4" />
          New Cluster
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="rq-card p-4 mb-4">
          <input
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Cluster name (e.g. Interviews, B-Roll, Aerials)"
            className="w-full rq-input-dark mb-2 text-sm"
            onKeyDown={e => e.key === 'Enter' && createCluster()}
          />
          <input
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            className="w-full rq-input-dark text-[var(--rq-text-soft)] mb-3 text-sm"
            onKeyDown={e => e.key === 'Enter' && createCluster()}
          />
          <div className="flex gap-2">
            <button
              onClick={createCluster}
              disabled={!newName.trim()}
              className="rq-btn-primary px-4 py-1.5 disabled:opacity-40 text-sm"
            >
              Create
            </button>
            <button
              onClick={() => { setShowCreate(false); setNewName(''); setNewDesc('') }}
              className="rq-btn-ghost px-4 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {clusters.length === 0 && !showCreate && (
        <div className="text-center py-16">
          <Layers className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No clusters yet</h2>
          <p className="text-[var(--rq-text-soft)] max-w-md mx-auto">
            Create clusters to group clips by theme — interviews, B-roll, aerials, etc.
          </p>
        </div>
      )}

      {/* Cluster cards with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
      <div className="space-y-4">
        {clusters.map(cluster => (
          <div
            key={cluster.id}
            className="rq-card overflow-hidden"
          >
            {/* Cluster header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              {editingId === cluster.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="rq-input-dark text-sm flex-1 px-2 py-1"
                    onKeyDown={e => e.key === 'Enter' && saveEdit(cluster.id)}
                  />
                  <button onClick={() => saveEdit(cluster.id)} className="text-green-400 hover:text-green-300 p-1">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <h3 className="text-[var(--rq-text)] font-medium text-sm">{cluster.name}</h3>
                    {cluster.description && (
                      <p className="text-xs text-[var(--rq-text-soft)] mt-0.5">{cluster.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-[var(--rq-text-soft)] mr-2">
                      {cluster.moments_count} clip{cluster.moments_count !== 1 ? 's' : ''}
                    </span>
                    {onGenerateDraft && (
                      <button
                        onClick={() => onGenerateDraft({ scope_type: 'cluster', scope_ref_id: cluster.id, label: `Cluster: ${cluster.name}`, project_id: cluster.project_id || projectIds[0] })}
                        className="text-[var(--rq-text-soft)] hover:text-[var(--brand-light)] p-1 transition-colors"
                        title="Generate AI Draft"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => openAddClipsModal(cluster.id)}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                      title="Add clips"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => { setEditingId(cluster.id); setEditName(cluster.name) }}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                      title="Rename"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteCluster(cluster.id)}
                      className="text-[var(--rq-text-soft)] hover:text-red-400 p-1 transition-colors"
                      title="Delete cluster"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Moments row */}
            {cluster.moments.length > 0 ? (
              <SortableContext
                id={`cluster-${cluster.id}`}
                items={cluster.moments.map(m => m.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="p-3 flex gap-3 overflow-x-auto">
                  {cluster.moments.map(moment => (
                    <SortableClusterClip
                      key={moment.id}
                      moment={moment}
                      onRemove={() => removeMoment(cluster.id, moment.id)}
                      onPreview={() => openFromMoment(moment)}
                    />
                  ))}
                </div>
              </SortableContext>
            ) : (
              <div className="p-4 text-center text-xs text-[var(--rq-text-soft)]">
                No clips yet — click [+] above to add clips.
              </div>
            )}
          </div>
        ))}
      </div>

      <DragOverlay>
        {activeMoment ? <ClusterClipOverlay moment={activeMoment} /> : null}
      </DragOverlay>
      </DndContext>

      {/* ---- Add Clips to Existing Cluster Modal ---- */}
      {addToClusterId !== null && addToCluster && (
        <div className="rq-modal-overlay">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeAddClipsModal}
          />

          <div className="relative w-full max-w-lg mx-4 rq-modal-card max-h-[85vh] flex flex-col">
            <Layers className="w-10 h-10 text-[var(--rq-text-soft)] mb-4" />
            <h2 className="text-xl font-bold text-[var(--rq-text)] mb-1">Add Clips</h2>
            <p className="text-sm text-[var(--rq-text-soft)] mb-5">
              Select clips to add to <span className="text-[var(--rq-text)] font-medium">{addToCluster.name}</span>
            </p>

            {/* Selected count banner */}
            {addSelectedIds.size > 0 && (
              <div className="flex items-center gap-3 bg-emerald-900/30 border border-emerald-700/40 rounded-xl px-4 py-3 mb-5">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-emerald-200">
                  {addSelectedIds.size} clip{addSelectedIds.size !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}

            {/* Clip picker */}
            <div className="flex-1 min-h-0 overflow-y-auto border border-white/10 rounded-xl bg-black/25 mb-5">
              {availableForAdd.length === 0 ? (
                <div className="text-center py-8 text-sm text-[var(--rq-text-soft)]">
                  All clips are already in this cluster.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 p-3">
                  {availableForAdd.map(m => {
                    const isSelected = addSelectedIds.has(m.id)
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggleMomentForAdd(m.id)}
                        className={`relative rounded-lg overflow-hidden border transition-all text-left ${
                          isSelected
                            ? 'border-[var(--btn)] ring-1 ring-[var(--btn)]'
                            : 'border-white/10 hover:border-white/25'
                        }`}
                      >
                        <div className="aspect-video bg-black/35">
                          {m.thumbnail_url ? (
                            <img src={m.thumbnail_url} alt={m.title || 'Clip'} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-5 h-5 text-[var(--rq-text-soft)]" />
                            </div>
                          )}
                          <div className={`absolute top-1 left-1 w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected
                              ? 'bg-[var(--btn)] border-[var(--btn)]'
                              : 'border-white/40 bg-black/30'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <div className="p-1">
                          <p className="text-[10px] text-[var(--rq-text-soft)] truncate">
                            {m.title || m.asset_filename || 'Untitled'}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeAddClipsModal}
                className="rq-btn-ghost px-5 py-2.5 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={addClipsToCluster}
                disabled={addSelectedIds.size === 0 || addingClips}
                className="rq-btn-primary px-5 py-2.5 disabled:opacity-40 text-sm"
              >
                {addingClips ? 'Adding...' : `Add ${addSelectedIds.size > 0 ? addSelectedIds.size + ' ' : ''}Clip${addSelectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <MediaPreviewModal preview={preview} onClose={closePreview} />
    </div>
  )
}
